import { get } from 'lodash';
import { Schema } from 'normalizr';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { APIRequestConfig } from '../../api-client/types';
import { ApiFlaggedAction } from '../middlewares/api-middleware';
import { ActionSet, FreeShapeAction, NormalisedData } from '../types';
import { deleteAction, deleteNormalisedStore, fetchAction, updateNormalisedStore } from './actions';
import { isCacheStale } from './cache-invalidation-utils';
import { NORMALISATION_FLAGS } from './constants';
import { DispatchAny } from './types';
import { getSchemaEntityKey, parseActionForNormalisation } from './utils';

/**
 * Abstract function to generate a method to check the current contents of the normalised store and,
 * if it finds something, return it, if not, call a fetch method to retrieve it from the server.
 *
 * @param {string} path - url to query
 * @param {ActionSet} actionSet - action set to call on retrieval from normalised store
 * @param {Schema} schema - futrli-sdk schema
 * @returns {ThunkAction}
 */
export const getFromNormalisedStoreByPath = (
  path: string,
  actionSet: ActionSet,
  schema: Schema,
  forceFetch = false
): DispatchAny => async (dispatch: ThunkDispatch<unknown, unknown, Action>, getState: () => unknown) => {
  const entityType = getSchemaEntityKey(schema);
  const state = get(getState(), 'normalised', {});
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  let ids: string[] | undefined = get(state, `requests.${entityType}.${path}`);
  const stale = isCacheStale(path);

  if (!ids || stale || forceFetch) {
    ids = await fetchFromRemote(schema, path, actionSet, dispatch);
  }

  return dispatch({
    type: actionSet.ACTION,
    payload: ids,
    meta: {
      schema
    }
  });
};

/**
 * This is where the actual fetch happens, if necessary.
 * Returns a list of ids that have been fetched from the server.
 *
 * @param {Schema} schema
 * @param {string} path
 * @param {ActionSet} actionSet
 * @param {ThunkDispatch} dispatch
 * @returns {string[]}
 */
export const fetchFromRemote = async (
  schema: Schema,
  path: string,
  actionSet: ActionSet,
  dispatch: ThunkDispatch<unknown, unknown, Action>
) => {
  const fetched = await fetchAction(schema, path, actionSet)(dispatch);
  const fetchedData: NormalisedData<unknown> = get(fetched, 'data', {});
  const result: string | string[] = fetchedData.result || [];

  const entity = getSchemaEntityKey(schema);

  await updateNormalisedStore(fetchedData, entity as unknown as string, path)(dispatch);

  return typeof result === 'string' ? [result] : result;
};

/**
 * Abstract function to run a sequential execution of async deletes or flush the whole entity.
 *
 * @param {string[]} ids - for simplicity we _always_ work with a list of strings
 * @param {string} entityType - entity type in normalised store
 * @param {ActionSet} actionSet - action set to call on retrieval from normalised store
 * @param {string} path - url to query
 * @param {normalizr.Entity | normalizr.Entity[] | undfined} schema - futrli-sdk schema
 * @returns {ThunkAction}
 */
export const deleteFromNormalisedStore = (
  ids: string[] | null,
  entityType: string,
  actionSet: ActionSet,
  path: string,
  schema?: Schema
): DispatchAny => async (dispatch: ThunkDispatch<unknown, unknown, Action>, getState: () => unknown) => {
  const state = getState();
  const normalisedEntities = get(state, 'normalised.entities', {}) as {
    [key: string]: { [key: string]: unknown };
  };
  const entities = get(normalisedEntities, entityType, {}) as {
    [key: string]: { [key: string]: unknown };
  };
  const requests = get(state, 'normalised.requests', {}) as {
    [key: string]: {
      [key: string]: string[] | string;
    };
  };
  const deletedIds: string[] = [];
  const idsToDelete: string[] = ids === null ? Object.keys(entities) : ids;

  if (!idsToDelete || !idsToDelete.length) {
    return;
  }

  // Sequential execution of async deletes
  await idsToDelete.reduce(async (promise: Promise<APIRequestConfig | void>, id: string) => {
    try {
      await promise;

      deletedIds.push(id);
    } catch {
      /* Swallowed Exception */
    }

    return deleteAction(path, actionSet, schema)(dispatch);
  }, Promise.resolve());

  deletedIds.map((id: string) => delete entities[id]);

  normalisedEntities[entityType] = entities;

  delete requests[entityType];

  deleteNormalisedStore({ requests, entities: normalisedEntities })(dispatch);

  if (Object.keys(entities).length === 0) {
    // Flushes the entity
    delete normalisedEntities[entityType];

    deleteNormalisedStore({ requests, entities: normalisedEntities }, true)(dispatch);
  }

  return dispatch({
    type: actionSet.ACTION,
    payload: deletedIds,
    meta: {
      schema
    }
  });
};

/**
 * Takes a given action and builds a normalisation action from given payload.
 *
 * @param {FreeShapeAction} action
 * @returns {FreeShapeAction | null}
 */
export const getNormalisationAction = (action: ApiFlaggedAction): FreeShapeAction | null => {
  const [normalisationAction, type, url] = parseActionForNormalisation(action);

  switch (normalisationAction) {
    case NORMALISATION_FLAGS.FLUSH_REQUEST_ENTITY:
      return {
        type: NORMALISATION_FLAGS.FLUSH_REQUEST_ENTITY,
        payload: {
          type
        }
      };
    case NORMALISATION_FLAGS.FLUSH_REQUEST_URL:
      return {
        type: NORMALISATION_FLAGS.FLUSH_REQUEST_URL,
        payload: {
          type,
          url
        }
      };
    default:
      return null;
  }
};
